import axios from 'axios';
import Constants from '../config/constants';

const API_BASE_URL = Constants.API_HOST;

// 토큰 가져오기 헬퍼 함수
const getAuthToken = (): string | null => {
  try {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      const user = JSON.parse(storedUser);
      return user.accessToken || null;
    }
  } catch (error) {
    console.error('토큰 가져오기 오류:', error);
  }
  return null;
};

export enum SocialPlatform {
  INSTAGRAM = 'instagram',
  FACEBOOK = 'facebook',
  THREADS = 'threads',
}

export enum PostStatus {
  PENDING = 'pending',
  POSTED = 'posted',
  FAILED = 'failed',
}

export interface SocialAccount {
  id: number;
  platform: SocialPlatform;
  username: string;
  isConnected: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SocialPost {
  id: number;
  reviewId: number;
  platform: SocialPlatform;
  postContent: string;
  hashtags: string[];
  imageUrls: string[];
  status: PostStatus;
  platformPostId?: string;
  errorMessage?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateSocialAccountRequest {
  platform: SocialPlatform;
  username: string;
  password: string;
}

export interface UpdateSocialAccountRequest {
  username?: string;
  password?: string;
  isConnected?: boolean;
}

export interface ReviewImageSelection {
  reviewId: number;
  imageUrls: string[];
}

export interface CreateSocialPostRequest {
  reviewIds: number[];
  platforms: SocialPlatform[];
  postContent: string;
  hashtags: string[];
  imageUrls: string[];
  selectedReviewImages?: ReviewImageSelection[];
}

export interface PostingStatusResponse {
  isPosted: boolean;
  postedPlatforms: string[];
  postedAt?: string;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  error?: string;
}

// OAuth 관련 API
export const oauthApi = {
  // OAuth 인증 URL 생성
  getAuthUrl: async (
    platform: SocialPlatform,
    redirectUri: string,
  ): Promise<{ authUrl: string }> => {
    try {
      const token = getAuthToken();
      const response = await axios.get(
        `${API_BASE_URL}/api/social/oauth/${platform}/auth-url`,
        {
          params: { redirectUri },
          headers: {
            access_token: token,
          },
        },
      );
      return response.data;
    } catch (error) {
      console.error('OAuth URL 생성 오류:', error);
      throw error;
    }
  },

  // OAuth 콜백 처리
  handleCallback: async (
    platform: SocialPlatform,
    code: string,
    redirectUri: string,
    userId: number,
  ): Promise<SocialAccount> => {
    try {
      const token = getAuthToken();
      const response = await axios.post(
        `${API_BASE_URL}/api/social/oauth/${platform}/callback`,
        { code, redirectUri, userId },
        {
          headers: {
            access_token: token,
          },
        },
      );
      return response.data;
    } catch (error) {
      console.error('OAuth 콜백 처리 오류:', error);
      throw error;
    }
  },
};

// 소셜 계정 관리 API
export const socialAccountApi = {
  // 지점의 소셜 계정 목록 조회
  getSocialAccounts: async (branchId: number): Promise<SocialAccount[]> => {
    try {
      const token = getAuthToken();
      const response = await axios.get<SocialAccount[]>(
        `${API_BASE_URL}/api/social/accounts/${branchId}`,
        {
          headers: {
            access_token: token,
          },
        },
      );
      return response.data;
    } catch (error) {
      console.error('소셜 계정 조회 오류:', error);
      throw error;
    }
  },

  // 소셜 계정 생성/업데이트
  createOrUpdateSocialAccount: async (
    branchId: number,
    data: CreateSocialAccountRequest,
  ): Promise<SocialAccount> => {
    try {
      const token = getAuthToken();
      const response = await axios.post<SocialAccount>(
        `${API_BASE_URL}/api/social/accounts/${branchId}`,
        data,
        {
          headers: {
            access_token: token,
          },
        },
      );
      return response.data;
    } catch (error) {
      console.error('소셜 계정 생성/업데이트 오류:', error);
      throw error;
    }
  },

  // 소셜 계정 수정
  updateSocialAccount: async (
    accountId: number,
    data: UpdateSocialAccountRequest,
  ): Promise<SocialAccount> => {
    try {
      const token = getAuthToken();
      const response = await axios.put<SocialAccount>(
        `${API_BASE_URL}/api/social/accounts/${accountId}`,
        data,
        {
          headers: {
            access_token: token,
          },
        },
      );
      return response.data;
    } catch (error) {
      console.error('소셜 계정 수정 오류:', error);
      throw error;
    }
  },

  // 소셜 계정 삭제
  deleteSocialAccount: async (
    accountId: number,
  ): Promise<{ success: boolean; message: string }> => {
    try {
      const token = getAuthToken();
      const response = await axios.delete<{
        success: boolean;
        message: string;
      }>(`${API_BASE_URL}/api/social/accounts/${accountId}`, {
        headers: {
          access_token: token,
        },
      });
      return response.data;
    } catch (error) {
      console.error('소셜 계정 삭제 오류:', error);
      throw error;
    }
  },
};

// 소셜 포스팅 관리 API
export const socialPostApi = {
  // 포스팅 전 계정 검증
  validatePostingAccounts: async (
    branchId: number,
    platforms: SocialPlatform[],
  ): Promise<{
    success: boolean;
    message: string;
    invalidPlatforms: string[];
  }> => {
    try {
      const token = getAuthToken();
      const response = await axios.post(
        `${API_BASE_URL}/api/social/posts/${branchId}/validate`,
        { platforms },
        {
          headers: {
            access_token: token,
          },
        },
      );
      return response.data;
    } catch (error) {
      console.error('계정 검증 오류:', error);
      throw error;
    }
  },

  // 소셜 포스팅 생성
  createSocialPost: async (
    branchId: number,
    data: CreateSocialPostRequest,
  ): Promise<SocialPost[]> => {
    try {
      const token = getAuthToken();
      const response = await axios.post<SocialPost[]>(
        `${API_BASE_URL}/api/social/posts/${branchId}`,
        data,
        {
          headers: {
            access_token: token,
          },
        },
      );
      return response.data;
    } catch (error) {
      console.error('소셜 포스팅 생성 오류:', error);
      throw error;
    }
  },

  // 지점의 소셜 포스팅 목록 조회
  getSocialPosts: async (branchId: number): Promise<SocialPost[]> => {
    try {
      const token = getAuthToken();
      const response = await axios.get<SocialPost[]>(
        `${API_BASE_URL}/api/social/posts/${branchId}`,
        {
          headers: {
            access_token: token,
          },
        },
      );
      return response.data;
    } catch (error) {
      console.error('소셜 포스팅 조회 오류:', error);
      throw error;
    }
  },

  // 리뷰별 포스팅 상태 조회
  getPostsByReview: async (reviewId: number): Promise<SocialPost[]> => {
    try {
      const token = getAuthToken();
      const response = await axios.get<SocialPost[]>(
        `${API_BASE_URL}/api/social/posts/review/${reviewId}`,
        {
          headers: {
            access_token: token,
          },
        },
      );
      return response.data;
    } catch (error) {
      console.error('리뷰별 포스팅 조회 오류:', error);
      throw error;
    }
  },

  // 리뷰 포스팅 상태 확인
  getReviewPostingStatus: async (
    reviewId: number,
  ): Promise<PostingStatusResponse> => {
    try {
      const token = getAuthToken();
      const response = await axios.get<PostingStatusResponse>(
        `${API_BASE_URL}/api/social/posts/review/${reviewId}/status`,
        {
          headers: {
            access_token: token,
          },
        },
      );
      return response.data;
    } catch (error) {
      console.error('리뷰 포스팅 상태 조회 오류:', error);
      throw error;
    }
  },
};
